/* GS Quick order — "Бърза поръчка" panel on the PDP (snippets/gs-quick-order.liquid, rendered by
   the buy-buttons block of sections/main-product.liquid). The panel is a Shopify contact form
   (name + phone) whose hidden fields tell the merchant which product the customer wants.
   This element opens/closes the panel from the trigger button(s) pointing at it via aria-controls,
   mirrors the theme's variant picker ('on:variant:change') into the variant + link fields, and
   copies the quantity from the product form when the form is sent. The form itself posts natively
   (Shopify redirects back with ?contact_posted=true and the success alert renders server-side),
   so nothing here talks to the network. */

if (!customElements.get('gs-quick-order')) {
  class GsQuickOrder extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.gs-quick-order__panel');
      this.form = this.querySelector('form');
      this.fields = {
        variant: this.querySelector('[data-gs-quick-order-field="variant"]'),
        quantity: this.querySelector('[data-gs-quick-order-field="quantity"]'),
        link: this.querySelector('[data-gs-quick-order-field="link"]')
      };
      this.triggers = Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));

      this.boundToggle = this.toggle.bind(this);
      this.triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', this.isOpen() ? 'true' : 'false');
        trigger.addEventListener('click', this.boundToggle);
      });

      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      document.addEventListener('on:variant:change', this.boundHandleVariantChange);

      if (this.form) this.form.addEventListener('submit', this.syncQuantity.bind(this));
    }

    disconnectedCallback() {
      document.removeEventListener('on:variant:change', this.boundHandleVariantChange);
      this.triggers.forEach((trigger) => trigger.removeEventListener('click', this.boundToggle));
    }

    isOpen() {
      return Boolean(this.panel) && !this.panel.hidden;
    }

    /**
     * The product form this panel belongs to (the main PDP form, not the quick-add drawer's).
     * Looked up on demand: the theme re-renders parts of it on variant change.
     * @returns {HTMLFormElement|null}
     */
    getProductForm() {
      return document.getElementById(this.dataset.productFormId);
    }

    /**
     * Opens or closes the panel; on open, focuses the first field and scrolls the card into view.
     */
    toggle() {
      if (!this.panel) return;
      const open = !this.isOpen();
      this.panel.hidden = !open;
      this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', open ? 'true' : 'false'));
      if (!open) return;

      this.syncQuantity();
      const firstField = this.panel.querySelector('input:not([type="hidden"])');
      if (firstField) firstField.focus({ preventScroll: true });
      this.revealPanel();
    }

    /**
     * Scrolls just far enough for the whole card (plus a small gap) to be on screen - but never so
     * far that the button which opened it slides under the sticky header. Keeping that button on
     * screen also keeps the product form on screen, so the mobile sticky add-to-cart bar (shown once
     * the form has scrolled off the top) never slides in over the Send button.
     */
    revealPanel() {
      const gap = 16;
      const rect = this.getBoundingClientRect();
      const overflow = rect.bottom + gap - window.innerHeight;
      if (overflow <= 0) return;

      // --header-end: the sticky header's bottom edge in the viewport, maintained by the theme header.
      const headerEnd = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-end')) || 0;
      const trigger = this.triggers[0];
      const anchorTop = trigger ? trigger.getBoundingClientRect().top : rect.top;
      const room = anchorTop - Math.max(headerEnd, 0) - gap;
      const delta = Math.min(overflow, room);
      if (delta <= 0) return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollBy({ top: delta, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    /**
     * Mirrors the picked variant into the hidden email fields.
     * @param {object} evt - 'on:variant:change' event dispatched by the theme's variant-picker.
     */
    handleVariantChange(evt) {
      // Only this section's picker: the quick-add drawer (overlay group) and any featured-product
      // section dispatch the same event for other products.
      const section = this.closest('.shopify-section');
      if (section && !section.contains(evt.target)) return;

      const { variant } = evt.detail;
      const parts = [];
      if (variant && variant.title && !this.hasAttribute('data-single-variant')) parts.push(variant.title);
      if (variant && variant.sku) parts.push(parts.length ? `(SKU ${variant.sku})` : `SKU ${variant.sku}`);

      if (this.fields.variant) this.fields.variant.value = parts.join(' ');
      if (this.fields.link) {
        this.fields.link.value = variant
          ? `${this.dataset.productUrl}?variant=${variant.id}`
          : this.dataset.productUrl;
      }
    }

    /**
     * Copies the quantity the customer set in the product form into the hidden quantity field.
     */
    syncQuantity() {
      const productForm = this.getProductForm();
      const qtyInput = productForm ? productForm.querySelector('[name="quantity"]') : null;
      if (qtyInput && this.fields.quantity) this.fields.quantity.value = qtyInput.value || '1';
    }
  }

  customElements.define('gs-quick-order', GsQuickOrder);
}
